import { ca_mission } from './ca-mission';

/**
 * The generator's warnings the mission page had no sentence for, and the one
 * place that turns any warning into what a person reads.
 *
 * The page showed «fallback:clear_area_generic:no_village» and
 * «enemy_replaced_irregular:OPF_F:OPF_G_F» as they were written (the owner,
 * 2026-10-11): `missionUi.page.warning` in `ca-mission.ts` answers the key
 * itself when it does not know it. The sentences that were missing are here,
 * in a block of their own, and not beside the others, only because another
 * open change (#380) edits that function at the same time; they can move
 * there once both are in. `web/src/generated/warning-keys.json` is every key
 * the generator writes, read from its sources by the platform's
 * `WarningKeysTest`, and `ca-warnings.test.ts` holds each to a sentence.
 */

const FALLBACKS: Record<string, string> = {
  ambush_qrf_no_post:
    'Es va demanar una reacció enemiga, però el mapa no té cap lloc a prop del tram, per carretera, d’on pugui sortir: l’emboscada es fa sense reacció.',
  cache_hunt_no_civilians:
    'El conjunt de mods no té cap civil d’aquesta època: al poble no hi ha confident ni famílies ni vigies, i el rastre el fan la pista i l’amagatall mateix.',
  cache_hunt_no_technical:
    'L’enemic no té cap cotxe lleuger armat d’aquesta època que es pugui aparcar: a la granja no hi ha vehicle armat, i els dos homes que el portarien seuen al foc.',
  cache_hunt_no_van:
    'L’enemic no té cap furgoneta ni cotxe lleuger sense armes d’aquesta època: a la porta de la granja no hi ha cap vehicle.',
  cache_hunt_technical:
    'El catàleg d’aquest conjunt de mods no s’ha llegit: el vehicle armat de la granja és el tot terreny armat del joc.',
  cache_hunt_van:
    'El catàleg d’aquest conjunt de mods no s’ha llegit: la furgoneta de la granja és la del joc.',
  cache_hunt_van_flight_no_road_out:
    'Es va demanar que la furgoneta fugís, però al voltant de la granja no hi ha terra ferma prou lluny on anar: aquesta nit la granja avisada es defensa amb el material.',
  cache_hunt_van_flight_no_van:
    'Es va demanar que la furgoneta fugís, però aquesta nit a la granja no hi ha cap furgoneta: la granja avisada es defensa amb el material.',
  capture_hvt_car:
    'El catàleg d’aquest conjunt de mods no s’ha llegit: el cotxe de l’oficial és el tot terreny del joc.',
  capture_hvt_no_car:
    'L’enemic no té cap cotxe sense armes d’aquesta època: l’oficial no té cotxe per fugir i espera a la casa.',
  capture_hvt_no_escort_car:
    'L’enemic no té cap cotxe lleuger armat d’aquesta època que es pugui aparcar: si l’oficial fuig, el seu cotxe surt sense escorta.',
  capture_hvt_no_way_out:
    'Al voltant de la casa no hi ha terra ferma prou lluny on anar: aquesta nit l’oficial no fuig en cotxe i espera a la casa.',
  convoy_unload_no_layout:
    'No s’ha pogut muntar la zona de descàrrega del comboi: la destinació té només la bandera i els homes que l’esperen.',
  csar_search_no_truck:
    'La facció enemiga no té cap camió al catàleg: els equips que busquen el pilot no arriben amb camió, sinó que apareixen al punt on haurien baixat.',
  era_civilians_stand_in:
    'El conjunt de mods no té civils d’aquesta època: la gent del poble i de la plaça són civils del joc mateix, amb roba senzilla.',
  forward_post_no_layout:
    'No s’ha pogut muntar el lloc avançat de la sortida: el punt d’extracció queda sense cap muntatge.',
  forward_post_no_vehicle:
    'La nostra facció no té cap cotxe ni cap camió al catàleg: el lloc avançat de la sortida no té cap vehicle.',
  indestructible_target:
    'La peça que es va triar com a objectiu a destruir no es pot destruir al joc: se n’ha posat una altra que sí.',
  near_spares_no_vehicle:
    'L’enemic no té cap cotxe lleuger ni cap camió sense armes al catàleg: no hi ha cap vehicle per agafar a prop de la zona de salt.',
  radar_no_reaction_post:
    'El mapa no té cap lloc a prop de l’estació, per carretera, d’on pugui sortir una reacció: la nit no en té.',
  radar_station_era:
    'El radar i el llançador de l’estació són del 2035 i aquesta missió és d’una altra època: l’objectiu és un radar genèric, sense l’estació muntada al seu voltant.',
  sabotage_no_reaction_post:
    'El mapa no té cap lloc a prop del parc de vehicles, per carretera, d’on pugui sortir una reacció: l’única reacció és la reserva a peu del mateix campament.',
  sabotage_no_pool_vehicle:
    'L’enemic no té cap vehicle sense armes al catàleg: al pati del parc no hi ha cap vehicle a la rampa del taller ni cap fila de reserva.',
  sabotage_no_spare_vehicle:
    'L’enemic no té cap cotxe lleuger sense armes al catàleg: al campament no hi ha cap vehicle per robar.',
  supply_control_road_point:
    'La ruta fins a la granja no passa per cap cruïlla de tres camins: el control de carretera és en un punt de la mateixa carretera, a un terç del trajecte.',
  supply_controls_off_the_way:
    'Només hi ha una carretera fins a la granja: els controls són a cruïlles d’altres carreteres, marcats igualment al mapa, i ningú no ha de passar-hi.',
  supply_truck:
    'La nostra facció no té cap camió propi al catàleg: les caixes van en un camió del joc, posat a la base.',
};

/** The bomb's own warnings with no value after them (`DefuseBombScript`). */
const BOMB_NOTES: Record<string, string> = {
  defuse_bomb_on_street:
    'Al voltant del lloc de valor no hi havia cap racó net fora de les carreteres: l’artefacte és al carrer, lluny dels edificis.',
  defuse_bomb_no_gun_houses:
    'Cap casa del poble veu l’artefacte des d’una finestra: els tiradors de la cèl·lula són al carrer, vigilant-lo.',
  defuse_bomb_no_crowd:
    'El conjunt de mods no té civils d’aquesta època: no hi ha ningú ajupit a la plaça, i la bomba només amenaça els jugadors.',
};

/** `fallback:defuse_bomb:<why>` (the places the script may do without) and `fallback:defuse_bomb_generic:<why>` (the night it cannot be written). */
const BOMB_FALLBACKS: Record<string, string> = {
  no_landmark:
    'Aquest poble no té cap església, capella, gasolinera ni hospital a prop del centre: l’artefacte és al mig del poble, en una parada de mercat.',
  no_safehouse:
    'El poble no té cap casa amb prou espai a uns centenars de metres del centre per a l’artificer: la missió no en té, i el cable bo s’ha de descobrir examinant l’artefacte.',
};
const BOMB_GENERIC: Record<string, string> = {
  no_survey:
    'Aquest mapa no té cap inspecció feta: la bomba és la genèrica, una maleta al punt del poble, sense tiradors a les finestres ni artificer.',
  needs_resurvey:
    'La inspecció d’aquest mapa és antiga i no diu on són les cases ni les esglésies: la bomba és la genèrica, una maleta al punt del poble. Cal tornar a inspeccionar el mapa.',
  no_village:
    'Aquest mapa no té cap poble habitat de dotze cases o més amb una carretera que en surti: la bomba és la genèrica, una maleta al punt del poble.',
};

const ALLIES: Record<string, string> = {
  'police:gendarmerie':
    'El conjunt de mods no té cap policia per al nostre bàndol: la policia que entra al final és la gendarmeria del joc.',
  'police:irregular':
    'El conjunt de mods no té cap policia per al nostre bàndol: en fan els irregulars del mateix bàndol.',
  'police:none':
    'El conjunt de mods no té policia, gendarmeria ni irregulars per al nostre bàndol: la missió s’ha fet sense la policia que havia d’entrar al final.',
  'partisans:dressed':
    'El conjunt de mods no té cap facció irregular per al nostre bàndol: els partisans són civils armats, amb la seva pròpia roba.',
  'partisans:none':
    'El conjunt de mods no té irregulars per al nostre bàndol ni civils per armar: la missió s’ha fet sense partisans.',
};

const STEPS: Record<string, string> = {
  humanitarian_aid: 'el repartiment d’ajuda',
  capture_hvt: 'la captura del cap',
};

/** The kinds of group the generator asks of a faction, as a person says them. */
const GROUP_KINDS: Record<string, string> = {
  Infantry: 'd’infanteria',
  Motorized: 'motoritzat',
  Mechanized: 'mecanitzat',
  Armored: 'blindat',
};

/** The kinds of arma fixa a faction can be asked for. */
const STATIC_KINDS: Record<string, string> = {
  hmg: 'metralladora pesada',
  gmg: 'llançagranades',
  at: 'antitanc',
  aa: 'antiaèria',
  mortar: 'morter',
};

/** Why a vehicle is no good for what it was picked for (`VehicleRoles.unfit`). */
function whyUnfit(why: string): string {
  if (why === 'medical') return 'és una ambulància';
  if (why === 'service')
    return 'és un vehicle de servei (combustible, reparació, munició, bombers)';
  if (why === 'ugv') return 'és un vehicle terrestre no tripulat';
  if (why.startsWith('seats')) return `només té ${why.split(':')[1] ?? 'poques'} places`;
  return 'no és un vehicle de terra';
}

/** What a vehicle was picked for (`FactionPreflight`). */
const UNFIT_FOR: Record<string, string> = {
  base: 'la base dels jugadors',
  enemy_ride: 'la reacció de l’enemic',
  post: 'un lloc de comandament de l’enemic',
};

/** The warnings of the faction and vehicle preflight (`FactionPreflight`), or null for any other. */
function preflightWarning(code: string): string | null {
  const [kind, what, extra, why] = code.split(':');
  switch (kind) {
    case 'profile_not_used':
      // profile_not_used:<enemy|player>:<profile id>: the profile the request named is gone or is another faction's.
      return what === 'enemy'
        ? 'La facció enemiga que es va triar ja no existeix: la missió s’ha fet amb l’enemic per defecte, no amb el que es demanava.'
        : 'La facció dels jugadors que es va triar ja no existeix o no és de la facció de la missió: els jugadors hi van amb la facció base, sense el seu equip propi.';
    case 'faction_unknown':
      // faction_unknown:<enemy|players>:<class>.
      return `${what === 'enemy' ? 'La facció enemiga' : 'La facció dels jugadors'} ${extra} no és al catàleg d’aquest conjunt de mods: la missió es fa amb el que el joc trobi, i pot no ser el que es volia.`;
    case 'faction_without_men':
      // faction_without_men:<enemy|players>:<class>.
      return what === 'enemy'
        ? `La facció enemiga ${extra} no té cap soldat al catàleg d’aquest conjunt de mods: els enemics surten amb el que el joc trobi, que pot ser d’una altra facció.`
        : `La facció ${extra} no té cap soldat al catàleg d’aquest conjunt de mods: les places dels jugadors surten amb la roba de sèrie de la missió.`;
    case 'squad_not_in_request':
      return 'La facció dels jugadors té una esquadra pròpia, però la petició no en portava cap: les places surten amb el kit de sèrie, no amb el de l’esquadra.';
    case 'squad_loadouts_lost':
      // squad_loadouts_lost:<n> | squad_loadouts_lost:aircrew.
      return what === 'aircrew'
        ? 'L’equipament dels pilots de la facció no ha arribat a les places dels pilots: hi surten amb el kit de sèrie.'
        : `L’esquadra triada dona equipament propi a ${what} places, però no li ha arribat a totes: les que falten surten amb el kit de sèrie.`;
    case 'roster_loadouts_lost':
      return `L’enemic triat vestia ${what} dels seus homes amb equipament propi, però no ha arribat a la missió: hi van amb el kit de sèrie de la seva classe.`;
    case 'enemy_group_missing':
      return `L’enemic no té cap grup ${GROUP_KINDS[what] ?? `«${what}»`} ni vehicles on posar-lo: la missió el fa amb el que el joc trobi, que pot ser d’una altra facció.`;
    case 'enemy_static_missing':
      return `L’enemic no té cap arma fixa ${STATIC_KINDS[what] ?? `«${what}»`} al catàleg: la que hi surt és la del joc, no la de la seva facció.`;
    case 'enemy_crew_missing':
      return 'L’enemic té vehicles blindats però cap tripulant al catàleg: els tripulants surten amb el que el joc trobi, que pot ser d’una altra facció.';
    case 'enemy_officer_missing':
      return 'L’enemic no té cap oficial al catàleg: els caps dels llocs de comandament són caps d’esquadra qualssevol.';
    case 'vehicle_unfit':
      // vehicle_unfit:<base|enemy_ride|post>:<class>:<why>.
      return `El vehicle ${extra} ${whyUnfit(why ?? '')} i s’ha triat per a ${UNFIT_FOR[what] ?? 'la missió'}: canvia’l al pas «Sortida» o escaneja de nou el conjunt de mods.`;
    case 'way_in_seats_short':
      // way_in_seats_short:<seats>:<players>.
      return `Els vehicles de la base només tenen ${what} places per a ${extra} jugadors: no tothom podrà entrar en vehicle.`;
    default:
      return null;
  }
}

/** The sentence of a warning `ca-mission.ts` has none for; null when this block has none either. */
export function moreWarnings(code: string): string | null {
  const preflight = preflightWarning(code);
  if (preflight !== null) return preflight;
  const [kind, what, extra] = code.split(':');
  if (code in BOMB_NOTES) return BOMB_NOTES[code];
  if (kind === 'fallback') {
    if (what === 'defuse_bomb') return BOMB_FALLBACKS[extra ?? ''] ?? null;
    if (what === 'defuse_bomb_generic') return BOMB_GENERIC[extra ?? ''] ?? null;
    if (what in FALLBACKS) return FALLBACKS[what];
    if (what?.startsWith('ally_')) {
      return ALLIES[`${what.slice('ally_'.length)}:${extra ?? ''}`] ?? null;
    }
    if (what === 'cache_hunt_generic') {
      // fallback:cache_hunt_generic:<why> (CacheHuntScript.genericInstead): the hunt's script needs the terrain's houses.
      return extra === 'needs_resurvey'
        ? 'La inspecció d’aquest terreny és anterior als edificis i cal repetir-la: la caça d’amagatalls s’ha fet com una recerca corrent, sense el poble, el rastre ni la granja.'
        : 'Aquest terreny encara no té cap inspecció acabada: la caça d’amagatalls s’ha fet com una recerca corrent, sense el poble, el rastre ni la granja.';
    }
    if (what === 'comms_blackout_generic') {
      // fallback:comms_blackout_generic:<why> (CommsBlackoutScript.genericInstead): the net's script needs the terrain's transmitters.
      return extra === 'needs_resurvey'
        ? 'La inspecció d’aquest terreny és anterior als transmissors i cal repetir-la: l’apagada de comunicacions s’ha fet com dues antenes corrents, sense la xarxa ni la reacció que en depèn.'
        : 'Aquest terreny encara no té cap inspecció acabada: l’apagada de comunicacions s’ha fet com dues antenes corrents, sense la xarxa ni la reacció que en depèn.';
    }
    if (what === 'stealth_infiltration') {
      // fallback:stealth_infiltration:no_rv_alternative (StealthScenes): no second or third place for the pick-up to move to.
      return extra === 'no_rv_alternative'
        ? 'El mapa no té cap altre lloc de recollida on l’enemic us pugui obligar a anar si us descobreix: el punt de recollida es queda on és.'
        : null;
    }
    if (what === 'recon_patrol_generic') {
      // fallback:recon_patrol_generic:<why> (ReconPatrolScript.genericInstead): the line needs the terrain's roads, heights and sites.
      return extra === 'needs_resurvey'
        ? 'La inspecció d’aquest terreny és anterior al que cal per fer la línia de la patrulla i cal repetir-la: la patrulla s’ha fet com una observació corrent, sense campament, lloc d’observació ni cruïlla.'
        : 'Aquest terreny encara no té cap inspecció acabada: la patrulla s’ha fet com una observació corrent, sense campament, lloc d’observació ni cruïlla.';
    }
    if (what === 'recon_patrol') {
      // fallback:recon_patrol:<what> (ReconPatrolScenes): the line has no height for the enemy's post, an area has no overlook for the
      // patrol, or the faction drives nothing to park in the harbour (the harbour is made without vehicles).
      if (extra === 'nai_height_missing') {
        return 'El mapa no té cap altura per al lloc d’observació enemic: la patrulla fa només dos punts d’observació, el campament i la cruïlla.';
      }
      if (extra === 'no_op') {
        return 'Algun dels punts d’observació no té cap lloc des d’on veure’l bé, i no porta marcat l’OP: la patrulla l’haurà de buscar.';
      }
      if (extra === 'no_harbour_vehicles') {
        return 'L’enemic no té cap vehicle per aparcar al campament: el campament s’ha fet sense vehicles ni xarxes, només amb la tenda del comandant, el combustible i la gent.';
      }
      return null;
    }
    if (what === 'era_pieces_kept') {
      // fallback:era_pieces_kept:<composition id> (EraPieces): pieces of 2035 with no stand-in in the era.
      return 'Algunes peces del muntatge no tenen equivalent d’aquesta època al conjunt de mods i s’hi han deixat les modernes.';
    }
    return null;
  }
  if (kind === 'civilians_off') {
    // civilians_off:no_era_civilians: the era has civilians of its own and the mod set carries none.
    return what === 'no_era_civilians'
      ? 'El conjunt de mods no té civils d’aquesta època: la missió s’ha fet sense civils, perquè no n’hi surtin de moderns.'
      : null;
  }
  if (kind === 'enemy_default_irregular') {
    // enemy_default_irregular:<faction class> (ClearAreaScript.enemyInstead).
    return `No es va triar cap enemic: aquest tipus de missió es lluita contra irregulars, i l’enemic és la facció irregular ${what}.`;
  }
  if (kind === 'enemy_replaced_irregular') {
    // enemy_replaced_irregular:<asked>:<plays>.
    return `L’enemic que es va demanar (${what}) és un exèrcit regular i aquest tipus de missió només es lluita contra irregulars: l’enemic és la facció irregular ${extra}.`;
  }
  if (kind === 'step_left_out') {
    // step_left_out:<objective type> (ClearAreaScript.dropStep): asked for by name, and the village's script has none.
    return what in STEPS
      ? `Es va demanar ${STEPS[what]}, però la neteja d’un poble amb sectors no té aquest pas: la missió s’ha fet sense.`
      : null;
  }
  if (code === 'mounted_patrol_on_foot') {
    // OrderOfBattle: the survey has no road within the runtime's reach of a mounted patrol's point.
    return 'A prop d’algun objectiu no hi ha cap carretera on posar la patrulla motoritzada de l’enemic: la fan els mateixos homes a peu.';
  }
  if (code === 'night_visibility_unmet:moon_not_measured') {
    return 'Els jugadors no porten visió nocturna i la nit hauria de tenir lluna plena i alta, però la lluna d’aquest terreny encara no s’ha mesurat: pot ser més fosca.';
  }
  return null;
}

/**
 * What a person reads for a warning: this block's sentence, else the mission
 * page's own; null when neither has one, so that whoever draws it says so
 * instead of printing the key (`WarningLine`).
 */
export function warningSentence(code: string): string | null {
  const more = moreWarnings(code);
  if (more !== null) return more;
  const said = ca_mission.missionUi.page.warning(code);
  // The older function answers the key itself for one it does not know.
  return said === code ? null : said;
}

/** What is shown for a warning with no sentence yet, with its key in small print beside it. */
export const UNKNOWN_WARNING =
  'El generador ha deixat un avís que aquesta pàgina encara no sap explicar.';
